import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, feedbackAPI, userAPI } from '../services/api';
import { User, Feedback, Project } from '../types';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState<User[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'ward_officer', county: '', ward_id: '' });

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const hasAccess = user && user.role === 'system_admin';

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [activeTab, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'users' || activeTab === 'system') {
        const usersRes = await adminAPI.getUsers();
        setUsers(usersRes.data);
      } else if (activeTab === 'feedback') {
        const feedbackRes = await feedbackAPI.getAdminFeedback();
        setFeedback(feedbackRes.data);
      } else if (activeTab === 'audit') {
        // Load audit logs
      } else if (activeTab === 'system') {
        // Load system configuration
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: any) => {
    try {
      await adminAPI.createUser(userData);
      setMessage('User created successfully');
      setNewUser({ name: '', email: '', password: '', role: 'ward_officer', county: '', ward_id: '' });
      loadData();
    } catch (error) {
      setMessage('Failed to create user');
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      await adminAPI.updateUser(userId, { role: newRole as any });
      setMessage('User role updated successfully');
      loadData();
    } catch (error) {
      setMessage('Failed to update user role');
    }
  };

  const toggleUserStatus = async (userId: string, isActive: boolean) => {
    try {
      await adminAPI.updateUser(userId, { is_active: !isActive });
      setMessage('User status updated successfully');
      loadData();
    } catch (error) {
      setMessage('Failed to update user status');
    }
  };

  const deleteFeedback = async (feedbackId: string) => {
    if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }
    
    try {
      await feedbackAPI.deleteFeedback(feedbackId);
      setMessage('Feedback deleted successfully');
      loadData(); // Reload feedback list
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      setMessage('Failed to delete feedback');
    }
  };

  const updateFeedbackStatus = async (feedbackId: string, status: string, isPublic?: boolean) => {
    try {
      const updateData: any = { status };
      if (isPublic !== undefined) {
        updateData.is_public = isPublic;
      }
      
      await feedbackAPI.updateFeedback(feedbackId, updateData);
      setMessage('Feedback status updated successfully');
      loadData();
    } catch (error) {
      console.error('Failed to update feedback:', error);
      setMessage('Failed to update feedback status');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await userAPI.delete(userId);
      setMessage('User deleted successfully');
      loadData();
    } catch (error) {
      setMessage('Failed to delete user');
    }
  };

  const resetPassword = async (userId: string, userName: string) => {
    const newPassword = prompt(`Enter new password for ${userName} (min 6 characters):`);
    if (!newPassword) return;
    if (newPassword.length < 6) { setMessage('Password must be at least 6 characters'); return; }
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_BASE}/admin/users/${userId}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) setMessage(data.message);
      else setMessage(data.error || 'Failed to reset password');
    } catch (error) {
      setMessage('Failed to reset password');
    }
  };

  const getFeedbackStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return '#4CAF50';
      case 'under_review': return '#FF9800';
      case 'resolved': return '#2196F3';
      case 'rejected': return '#F44336';
      default: return '#757575';
    }
  };

  if (!hasAccess) {
    return (
      <div className="admin-page">
        <header className="page-header">
          <div className="icon icon-primary">🔒</div>
          <h1>System Administrator Access Required</h1>
          <p>Only System Administrators can access this page</p>
        </header>
        <div className="access-denied">
          <p>This page is restricted to System Administrators only.</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary">Staff Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="page-header">
        <div className="icon icon-primary">⚙️</div>
        <h1>System Administrator Dashboard</h1>
        <p>Maintain platform infrastructure and user access</p>
      </header>

      {message && (
        <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          User Management
        </button>
        <button 
          className={activeTab === 'roles' ? 'active' : ''}
          onClick={() => setActiveTab('roles')}
        >
          Role Assignment
        </button>
        <button 
          className={activeTab === 'feedback' ? 'active' : ''}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback Management
        </button>
        <button 
          className={activeTab === 'system' ? 'active' : ''}
          onClick={() => setActiveTab('system')}
        >
          System Configuration
        </button>
        <button 
          className={activeTab === 'audit' ? 'active' : ''}
          onClick={() => setActiveTab('audit')}
        >
          Audit Logs
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <section className="user-management">
            <h2>User Management</h2>
            
            <div className="create-user-form">
              <h3>Create New User</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                createUser(newUser);
              }}>
                <div className="form-row">
                  <input 
                    type="text" 
                    placeholder="Full Name" 
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    required 
                  />
                  <input 
                    type="email" 
                    placeholder="Email" 
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required 
                  />
                </div>
                <div className="form-row">
                  <input 
                    type="password" 
                    placeholder="Password" 
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required 
                  />
                  <select 
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    required
                  >
                    <option value="ward_officer">Civic Moderator</option>
                    <option value="county_officer">Institutional Responder</option>
                    <option value="manager">Manager</option>
                    <option value="system_admin">System Administrator</option>
                  </select>
                </div>
                <div className="form-row">
                  <input 
                    type="text" 
                    placeholder="County (if applicable)" 
                    value={newUser.county}
                    onChange={(e) => setNewUser({...newUser, county: e.target.value})}
                  />
                  <input 
                    type="text" 
                    placeholder="Ward ID (if applicable)" 
                    value={newUser.ward_id}
                    onChange={(e) => setNewUser({...newUser, ward_id: e.target.value})}
                  />
                </div>
                <button type="submit">Create User</button>
              </form>
            </div>

            {loading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="users-list">
                <h3>Existing Users</h3>
                {users.map(user => (
                  <div key={user.id} className="user-item">
                    <div className="user-info">
                      <strong>{user.name}</strong>
                      <span className="user-email">{user.email}</span>
                      <span className={`role-badge ${user.role}`}>{user.role.replace('_', ' ')}</span>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="user-actions">
                      <select 
                        value={user.role}
                        onChange={(e) => updateUserRole(user.id, e.target.value)}
                      >
                        <option value="ward_officer">Civic Moderator</option>
                        <option value="county_officer">Institutional Responder</option>
                        <option value="manager">Manager</option>
                        <option value="system_admin">System Administrator</option>
                      </select>
                      <button 
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        className={user.is_active ? 'deactivate-btn' : 'activate-btn'}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => resetPassword(user.id, user.name)}
                        className="btn btn-secondary"
                      >
                        🔑 Reset Password
                      </button>
                      <button
                        onClick={() => deleteUser(user.id)}
                        className="delete-btn"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'feedback' && (
          <section className="feedback-management">
            <h2>Community Feedback Management</h2>
            
            {loading ? (
              <div className="loading">Loading feedback...</div>
            ) : (
              <div className="feedback-list">
                <div className="feedback-stats">
                  <div className="stat-card">
                    <h3>{feedback.length}</h3>
                    <p>Total Feedback</p>
                  </div>
                  <div className="stat-card">
                    <h3>{feedback.filter(f => f.status === 'submitted').length}</h3>
                    <p>Pending Review</p>
                  </div>
                  <div className="stat-card">
                    <h3>{feedback.filter(f => f.is_public).length}</h3>
                    <p>Public</p>
                  </div>
                  <div className="stat-card">
                    <h3>{feedback.filter(f => f.is_anonymous).length}</h3>
                    <p>Anonymous</p>
                  </div>
                </div>

                <div className="feedback-items">
                  {feedback.map(item => (
                    <div key={item.id} className="feedback-item">
                      <div className="feedback-header">
                        <div className="feedback-meta">
                          <strong>{item.is_anonymous ? 'Anonymous' : item.name}</strong>
                          <span className="feedback-type">{item.feedback_type}</span>
                          <span className="feedback-location">{item.ward}, {item.county}</span>
                          <span className="feedback-date">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="feedback-status">
                          <span 
                            className={`status-badge ${item.status}`}
                            style={{ backgroundColor: getFeedbackStatusColor(item.status) }}
                          >
                            {item.status}
                          </span>
                          {item.is_public && <span className="public-badge">Public</span>}
                        </div>
                      </div>
                      
                      <div className="feedback-content">
                        <p>{item.message}</p>
                        {item.project_reference && (
                          <p className="project-ref">📋 Project: {item.project_reference}</p>
                        )}
                      </div>
                      
                      <div className="feedback-actions">
                        <select 
                          value={item.status}
                          onChange={(e) => updateFeedbackStatus(item.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="submitted">Submitted</option>
                          <option value="under_review">Under Review</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="resolved">Resolved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        
                        <button 
                          onClick={() => updateFeedbackStatus(item.id, item.status, !item.is_public)}
                          className={`visibility-btn ${item.is_public ? 'make-private' : 'make-public'}`}
                        >
                          {item.is_public ? '👁️ Make Private' : '🔒 Make Public'}
                        </button>
                        
                        <button 
                          onClick={() => deleteFeedback(item.id)}
                          className="delete-btn"
                          title="Delete feedback"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {feedback.length === 0 && (
                    <div className="no-feedback">
                      <p>No feedback submissions found.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'roles' && (
          <section className="role-management">
            <h2>Role Assignment</h2>
            <div className="role-info">
              <div className="role-card">
                <h3>System Administrator</h3>
                <p>Maintain platform infrastructure and user access</p>
                <ul>
                  <li>✓ Manage users</li>
                  <li>✓ Assign roles</li>
                  <li>✓ Configure system settings</li>
                  <li>✓ View all system data</li>
                  <li>✓ Maintain audit logs</li>
                  <li>❌ Cannot edit official budget documents</li>
                  <li>❌ Cannot modify project allocations</li>
                </ul>
              </div>
              <div className="role-card">
                <h3>Manager</h3>
                <p>Oversight & governance - ensure data accuracy and integrity</p>
                <ul>
                  <li>✓ Approve uploaded budget documents</li>
                  <li>✓ Approve project allocation entries</li>
                  <li>✓ Review citizen feedback</li>
                  <li>✓ Request corrections from officers</li>
                  <li>✓ Generate analytical reports</li>
                  <li>❌ Cannot alter original documents</li>
                </ul>
              </div>
              <div className="role-card">
                <h3>Institutional Responder</h3>
                <p>Manage county-level budget data and documents</p>
                <ul>
                  <li>✓ Upload budget documents</li>
                  <li>✓ Enter project allocations</li>
                  <li>✓ Categorize projects by sector</li>
                  <li>✓ View all ward submissions</li>
                  <li>✓ Respond to county-level issues</li>
                  <li>❌ Cannot approve data publication</li>
                  <li>❌ Cannot remove approved data</li>
                </ul>
              </div>
              <div className="role-card">
                <h3>Civic Moderator</h3>
                <p>Handle citizen engagement and verification at ward level</p>
                <ul>
                  <li>✓ Monitor ward feedback</li>
                  <li>✓ Respond to citizen questions</li>
                  <li>✓ Verify reported issues</li>
                  <li>✓ Upload field evidence</li>
                  <li>✓ Update project progress</li>
                  <li>❌ Cannot edit budget documents</li>
                  <li>❌ Cannot approve project data</li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'system' && (
          <section className="system-config">
            <h2>System Configuration</h2>

            <div className="config-section">
              <h3>🏥 Platform Info</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div className="stat-card">
                  <h3>{users.length}</h3><p>Total Users</p>
                </div>
                <div className="stat-card">
                  <h3>{users.filter(u => u.is_active).length}</h3><p>Active Users</p>
                </div>
                <div className="stat-card">
                  <h3>{users.filter(u => u.role === 'ward_officer').length}</h3><p>Civic Moderators</p>
                </div>
                <div className="stat-card">
                  <h3>{users.filter(u => u.role === 'community_volunteer').length}</h3><p>Volunteers</p>
                </div>
              </div>
            </div>

            <div className="config-section">
              <h3>🔑 Default Login Credentials</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '0.6rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Role</th>
                    <th style={{ padding: '0.6rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Email</th>
                    <th style={{ padding: '0.6rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Default Password</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { role: 'System Admin', email: 'admin@turkana.go.ke', password: 'admin123' },
                    { role: 'Institutional Responder', email: 'county@turkana.go.ke', password: 'county123' },
                    { role: 'Civic Moderator (Kakuma)', email: 'kakuma@turkana.go.ke', password: 'ward123' },
                    { role: 'Civic Moderator (Lodwar)', email: 'lodwar@turkana.go.ke', password: 'ward123' },
                    { role: 'Volunteer', email: 'volunteer@turkana.go.ke', password: 'volunteer123' },
                  ].map((cred, i) => (
                    <tr key={i}>
                      <td style={{ padding: '0.6rem', border: '1px solid #e0e0e0' }}>{cred.role}</td>
                      <td style={{ padding: '0.6rem', border: '1px solid #e0e0e0', fontFamily: 'monospace' }}>{cred.email}</td>
                      <td style={{ padding: '0.6rem', border: '1px solid #e0e0e0', fontFamily: 'monospace' }}>{cred.password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <small style={{ color: '#e53935', marginTop: '0.5rem', display: 'block' }}>⚠️ Change default passwords immediately using the Reset Password button in User Management.</small>
            </div>

            <div className="config-section">
              <h3>🛡️ Security Actions</h3>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => {
                  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                  fetch(`${API_BASE}/health`).then(r => r.json()).then(d => setMessage(`System health: ${d.status} | DB: ${d.database}`));
                }}>Check System Health</button>
                <button className="btn btn-secondary" onClick={() => {
                  if (window.confirm('Force re-seed the database? This will add missing default users and wards.')) {
                    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                    fetch(`${API_BASE}/seed-database`, { method: 'POST' })
                      .then(r => r.json())
                      .then(d => setMessage(d.message || 'Seeding complete'));
                  }
                }}>Re-seed Database</button>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'audit' && (
          <section className="audit-logs">
            <h2>Audit Logs</h2>
            <div className="audit-filters">
              <input type="date" placeholder="From Date" />
              <input type="date" placeholder="To Date" />
              <select>
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="approve">Approve</option>
              </select>
              <button className="btn btn-primary">Filter</button>
            </div>
            <div className="audit-list">
              <p>Audit log functionality will be implemented here</p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminPage;